import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { COUNTRY_DIAL_CODES, DEFAULT_DIAL_ISO2 } from '../../lib/countryDialCodes';
import { cn } from '../../lib/utils';

import { FieldChrome } from './FieldChrome';
import { baseControlClasses } from './Input';

// Same country list `PhoneNumberField` uses, Sri Lanka pinned first, the
// rest alphabetical — but only the country name is relevant here (no
// dial code/flag), so this is a plain label list rather than a
// dial-code picker.
const COUNTRY_LABELS: string[] = [
  COUNTRY_DIAL_CODES.find((c) => c.iso2 === DEFAULT_DIAL_ISO2)!.label,
  ...COUNTRY_DIAL_CODES.filter((c) => c.iso2 !== DEFAULT_DIAL_ISO2)
    .map((c) => c.label)
    .sort((a, b) => a.localeCompare(b)),
];

// Radix forbids an empty-string Item value (reserved for "no selection");
// remapped to this sentinel at the Radix boundary only, same pattern as
// `Select.tsx`/`PhoneNumberField.tsx`.
const EMPTY_VALUE_SENTINEL = '__brainkey_country_empty__';

const itemClasses = cn(
  'relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer select-none outline-none transition-colors duration-150',
  'hover:bg-brand-50 dark:hover:bg-white/5 data-[highlighted]:bg-brand-50 dark:data-[highlighted]:bg-white/5',
);

export interface CountryFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  optional?: boolean;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
}

/** Country picker over the same ~195-country list `PhoneNumberField` uses.
 * A plain `Select` can't hold a list this long — with no bounded height or
 * type-ahead it either clips most of the list or is unscrollable, same
 * problem `PhoneNumberField`'s own doc comment already solved for the
 * dial-code picker ("scrolling a 195-item list is unusable"). This mirrors
 * that same fix: a bounded, scrollable list with a text filter. */
export function CountryField({
  label = 'Country',
  hint,
  optional,
  value,
  onChange,
  id,
  disabled,
}: CountryFieldProps): JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const [filter, setFilter] = useState('');

  const filteredLabels = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q === '') return COUNTRY_LABELS;
    return COUNTRY_LABELS.filter((label) => label.toLowerCase().includes(q));
  }, [filter]);

  return (
    <FieldChrome label={label} hint={hint} optional={optional} id={fieldId}>
      <SelectPrimitive.Root
        value={value === '' ? EMPTY_VALUE_SENTINEL : value}
        onValueChange={(next) => onChange(next === EMPTY_VALUE_SENTINEL ? '' : next)}
        disabled={disabled ?? false}
        onOpenChange={(open) => {
          if (!open) setFilter('');
        }}
      >
        <SelectPrimitive.Trigger
          id={fieldId}
          className={cn(
            baseControlClasses,
            'flex items-center justify-between gap-2 text-left',
            'focus:ring-zinc-900/10 dark:focus:ring-brand-500/20',
            'data-[state=open]:ring-2 data-[state=open]:ring-zinc-900/10 dark:data-[state=open]:ring-brand-500/20',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          <SelectPrimitive.Value placeholder="Not specified">
            {value === '' ? 'Not specified' : value}
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon>
            <ChevronDown className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={6}
            className="z-[1100] overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 shadow-soft dark:border-white/10 dark:bg-zinc-900 dark:shadow-glass-dark min-w-[var(--radix-select-trigger-width)]"
          >
            <div className="flex items-center gap-2 border-b border-zinc-200 px-3 py-2 dark:border-white/10">
              <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400 dark:text-zinc-500" />
              <input
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation();
                }}
                placeholder="Search country…"
                className="w-full bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-zinc-100 dark:placeholder:text-zinc-500"
              />
            </div>
            <SelectPrimitive.Viewport className="max-h-72 p-1.5">
              <SelectPrimitive.Item value={EMPTY_VALUE_SENTINEL} className={itemClasses}>
                <SelectPrimitive.ItemText>Not specified</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="ml-auto">
                  <Check className="w-3.5 h-3.5 text-brand-500" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
              {filteredLabels.length === 0 && (
                <p className="px-3 py-2 text-xs text-zinc-400 dark:text-zinc-500">
                  No countries match “{filter}”.
                </p>
              )}
              {filteredLabels.map((countryLabel) => (
                <SelectPrimitive.Item
                  key={countryLabel}
                  value={countryLabel}
                  className={itemClasses}
                >
                  <SelectPrimitive.ItemText>{countryLabel}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="ml-auto">
                    <Check className="w-3.5 h-3.5 text-brand-500" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </FieldChrome>
  );
}
